import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class MarkdownConverterService {

    constructor() { }

    /**
     * Converts HTML content to markdown format
     * @param html The HTML content to convert
     * @returns Markdown formatted string
     */
    htmlToMarkdown(html: string): string {
        if (!html) return '';

        // Create a temporary div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        let result = this.convertNodeToMarkdown(tempDiv);

        // Clean up excessive newlines but preserve empty lines
        result = result.replace(/\n{3,}/g, '\n\n'); // Remove this line to preserve empty lines
        result = result.replace(/^\n+/, ''); // Remove leading newlines
        result = result.replace(/\n+$/, ''); // Remove trailing newlines

        return result;
    }

    /**
     * Converts a DOM node to markdown
     * @param node The DOM node to convert
     * @returns Markdown formatted string
     */
    private convertNodeToMarkdown(node: Node): string {
        if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent || '';
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            const tagName = element.tagName.toLowerCase();

            switch (tagName) {
                case 'div':
                    // For div elements, just process children without adding extra newlines
                    return this.getChildContent(element);

                case 'p':
                    const content = this.getChildContent(element);
                    // Don't add newline if this paragraph is inside a list item
                    const isInsideListItem = element.closest('li') !== null;
                    return content + (isInsideListItem ? '' : '\n');

                case 'br':
                    return '\n';

                case 'strong':
                case 'b':
                    return '**' + this.getChildContent(element) + '**';

                case 'em':
                case 'i':
                    return '*' + this.getChildContent(element) + '*';

                case 'u':
                    return '__' + this.getChildContent(element) + '__';

                case 'code':
                    return '`' + this.getChildContent(element) + '`';

                case 'a':
                    const href = element.getAttribute('href');
                    const text = this.getChildContent(element);
                    if (href) {
                        return `[${text}](${href})`;
                    } else {
                        return text;
                    }

                case 'ul':
                    return this.convertListToMarkdown(element, false);

                case 'ol':
                    return this.convertListToMarkdown(element, true);

                case 'li':
                    // This is handled in convertListToMarkdown
                    return this.getChildContent(element);

                case 'h1':
                case 'h2':
                case 'h3':
                case 'h4':
                case 'h5':
                case 'h6':
                    const level = parseInt(tagName.charAt(1));
                    const headerContent = this.getChildContent(element);
                    return '#'.repeat(level) + ' ' + headerContent + '\n\n';

                case 'blockquote':
                    const quoteContent = this.getChildContent(element);
                    return '> ' + quoteContent.replace(/\n/g, '\n> ') + '\n\n';

                default:
                    // For unknown tags, just get the text content
                    return this.getChildContent(element);
            }
        }

        // For other node types, process child nodes
        let markdown = '';
        for (const child of Array.from(node.childNodes)) {
            markdown += this.convertNodeToMarkdown(child);
        }
        return markdown;
    }

    /**
     * Gets the text content of child nodes
     * @param element The HTML element
     * @returns Text content
     */
    private getChildContent(element: HTMLElement): string {
        let content = '';
        for (const child of Array.from(element.childNodes)) {
            content += this.convertNodeToMarkdown(child);
        }
        return content;
    }

    /**
     * Converts HTML lists to markdown
     * @param listElement The list element (ul or ol)
     * @param isOrdered Whether it's an ordered list
     * @returns Markdown formatted list
     */
    private convertListToMarkdown(listElement: HTMLElement, isOrdered: boolean): string {
        let markdown = '';
        const items = listElement.querySelectorAll('li');

        items.forEach((item, index) => {
            const content = this.getChildContent(item).trim();
            if (isOrdered) {
                markdown += `${index + 1}. ${content}\n`;
            } else {
                markdown += `- ${content}\n`;
            }
        });

        return markdown + '\n';
    }

    /**
     * Cleans HTML content by removing unwanted tags and attributes
     * @param html The HTML content to clean
     * @returns Cleaned HTML
     */
    cleanHtml(html: string): string {
        if (!html) return '';

        return html
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
            .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
            .replace(/<[^>]*>/g, (match) => {
                // Keep only basic formatting tags
                const allowedTags = ['b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'p', 'br', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code'];
                const tagName = match.replace(/[<>\/]/g, '');
                return allowedTags.includes(tagName) ? match : '';
            })
            .replace(/\s+/g, ' ')
            .trim();
    }

    /**
     * Test method to verify HTML to markdown conversion
     * @param html The HTML to test
     * @returns The converted markdown
     */
    testConversion(html: string): string {
        console.log('Testing HTML to Markdown conversion:');
        console.log('Input HTML:', html);
        const result = this.htmlToMarkdown(html);
        console.log('Output Markdown:', result);
        console.log('Output Markdown (escaped):', JSON.stringify(result));
        return result;
    }
} 